/**
 * Animated "Level up" icon. Same layers as {@link ImprovePromptIcon} in green/blue, with rising
 * chevrons in the centre.
 */
export const LevelUpAnimatedIcon = () => (
  <div className="level-up-icon relative flex h-5 w-5 items-center justify-center" aria-hidden="true">
    <style>{ICON_STYLES}</style>
    <div className="level-up-glow absolute inset-[-2px] rounded-full" />
    <div className="level-up-blob absolute inset-0" />
    <div className="level-up-blob-alt absolute inset-[18%]" />
    <svg className="level-up-chevrons absolute inset-[18%]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        className="level-up-chevron-top"
        d="M5 14 L12 7 L19 14"
        stroke="white"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        className="level-up-chevron-bottom"
        d="M5 20 L12 13 L19 20"
        stroke="white"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const ICON_STYLES = `
.level-up-icon { overflow: visible; }

/* Emerald → cyan → indigo, so it doesn't look like the Improve prompt icon. */
.level-up-glow {
  background: radial-gradient(
    circle,
    rgba(0, 196, 140, 0.32) 0%,   /* emerald */
    rgba(0, 184, 217, 0.22) 45%,  /* cyan */
    rgba(61, 90, 254, 0.15) 75%,  /* indigo */
    transparent 100%
  );
  filter: blur(3px);
  animation: level-up-glow-pulse 3.2s ease-in-out infinite;
}

.level-up-blob {
  background: conic-gradient(
    from 0deg,
    #00C48C 0deg,     /* emerald */
    #00B8D9 120deg,   /* cyan */
    #3D5AFE 240deg,   /* indigo */
    #00C48C 360deg    /* emerald (loop back) */
  );
  border-radius: 30% 65% 25% 70% / 55% 25% 65% 30%;
  animation:
    level-up-morph 5s ease-in-out infinite,
    level-up-spin 9s linear infinite;
  filter: blur(0.4px);
}

.level-up-blob-alt {
  background: conic-gradient(
    from 180deg,
    rgba(255, 255, 255, 0.95),
    rgba(0, 184, 217, 0.7),   /* cyan hot spot */
    rgba(0, 196, 140, 0.85),  /* emerald hot spot */
    rgba(255, 255, 255, 0.95)
  );
  border-radius: 65% 25% 55% 35% / 30% 60% 30% 60%;
  animation:
    level-up-morph-alt 4s ease-in-out infinite,
    level-up-spin 5.5s linear infinite reverse;
  mix-blend-mode: screen;
}

/* Two chevrons rising on the same 1.6s cycle, offset by half a cycle. */
.level-up-chevrons {
  filter: drop-shadow(0 0 1px rgba(255, 255, 255, 0.9));
}
.level-up-chevron-top {
  transform-origin: 50% 50%;
  animation: level-up-chevron-rise 1.6s ease-in-out infinite;
}
.level-up-chevron-bottom {
  transform-origin: 50% 50%;
  animation: level-up-chevron-rise 1.6s ease-in-out infinite;
  animation-delay: -0.8s;
}

@keyframes level-up-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

@keyframes level-up-morph {
  0%, 100% { border-radius: 30% 65% 25% 70% / 55% 25% 65% 30%; }
  33%      { border-radius: 65% 30% 70% 25% / 25% 65% 30% 55%; }
  66%      { border-radius: 45% 55% 40% 60% / 60% 40% 55% 45%; }
}

@keyframes level-up-morph-alt {
  0%, 100% { border-radius: 65% 25% 55% 35% / 30% 60% 30% 60%; }
  50%      { border-radius: 25% 65% 35% 55% / 60% 30% 60% 30%; }
}

@keyframes level-up-glow-pulse {
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50%      { opacity: 0.85; transform: scale(1.08); }
}

/* Rise and fade out, looping. */
@keyframes level-up-chevron-rise {
  0%   { transform: translateY(30%); opacity: 0; }
  25%  { transform: translateY(0);   opacity: 1; }
  75%  { transform: translateY(-30%); opacity: 1; }
  100% { transform: translateY(-60%); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .level-up-glow,
  .level-up-blob,
  .level-up-blob-alt,
  .level-up-chevron-top,
  .level-up-chevron-bottom {
    animation: none;
  }
}
`;
